/**
 * App modal:
 *
 * Centered dialog shell rendered in `_layouts/base.html` (`[data-app-modal]`).
 * `showModal()` supplies the dimmed backdrop and Escape-to-close.
 *
 * Content markup uses app_modal_panel / header / body / footer in
 * `_macros/common/components.html`.
 *
 * Usage:
 *   window.appModal.open(nodeOrHtml)
 *   window.appModal.close()
 *
 * Elements with [data-app-modal-close] inside the modal close it on click.
 * A <template data-app-modal-auto-open> on the page is cloned and opened on load.
 * An open modal dialog locks scroll on the page behind it; a scrollable
 * modal body still scrolls.
 */

function getDialog() {
  return document.querySelector("[data-app-modal]");
}

function scrollableInModal(target, modal) {
  let el = target instanceof Element ? target : null;
  while (el && el !== modal) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

let pinnedScroll = null;

function pinBackgroundScroll() {
  if (pinnedScroll || !document.querySelector("dialog:modal")) return;
  pinnedScroll = { x: window.scrollX, y: window.scrollY };
  const body = document.body;
  body.style.position = "fixed";
  body.style.top = `-${pinnedScroll.y}px`;
  body.style.left = `-${pinnedScroll.x}px`;
  body.style.width = "100%";
}

function unpinBackgroundScroll() {
  if (!pinnedScroll || document.querySelector("dialog:modal")) return;
  const { x, y } = pinnedScroll;
  pinnedScroll = null;
  const body = document.body;
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.width = "";
  window.scrollTo(x, y);
}

function syncBackgroundScroll() {
  if (document.querySelector("dialog:modal")) pinBackgroundScroll();
  else unpinBackgroundScroll();
}

/** True when a wheel should not move the page behind an open modal dialog. */
function backgroundScrollShouldLock(deltaY, scroller) {
  if (!scroller) return true;
  if (!deltaY) return false;
  const max = scroller.scrollHeight - scroller.clientHeight;
  if (deltaY < 0 && scroller.scrollTop <= 0) return true;
  if (deltaY > 0 && scroller.scrollTop >= max - 1) return true;
  return false;
}

function preventBackgroundWheel(event) {
  const modal = document.querySelector("dialog:modal");
  if (!modal) return;
  const scroller = scrollableInModal(event.target, modal);
  if (backgroundScrollShouldLock(event.deltaY, scroller)) event.preventDefault();
}

function preventBackgroundTouch(event) {
  const modal = document.querySelector("dialog:modal");
  if (!modal) return;
  if (!scrollableInModal(event.target, modal)) event.preventDefault();
}

function getContent() {
  return getDialog()?.querySelector("[data-app-modal-content]") ?? null;
}

function openAppModal(content) {
  const dialog = getDialog();
  const slot = getContent();
  if (!dialog || !slot) return;

  // Removing the focused control inside an open dialog closes it in Safari,
  // which flashes the backdrop when one modal's content replaces another's.
  const active = document.activeElement;
  if (
    dialog.open &&
    active instanceof Node &&
    active !== dialog &&
    dialog.contains(active)
  ) {
    dialog.focus({ preventScroll: true });
  }

  slot.replaceChildren();
  if (content instanceof Node) {
    slot.appendChild(content);
  } else if (typeof content === "string") {
    slot.innerHTML = content;
  }

  if (!dialog.open) dialog.showModal();
  // A focused control can scroll this dialog; the next open must start at the top.
  dialog.scrollTop = 0;
}

function closeAppModal() {
  const dialog = getDialog();
  if (dialog?.open) dialog.close();
}

function clearContent() {
  getContent()?.replaceChildren();
}

function maybeAutoOpen() {
  const template = document.querySelector("[data-app-modal-auto-open]");
  const content = template?.content?.firstElementChild?.cloneNode(true);
  if (!content) return;
  openAppModal(content);
}

function initAppModal() {
  const dialog = getDialog();
  if (!dialog || dialog.dataset.initialized === "true") return;
  dialog.dataset.initialized = "true";

  dialog.addEventListener("click", (event) => {
    const closer = event.target.closest("[data-app-modal-close]");
    if (closer && dialog.contains(closer)) {
      event.preventDefault();
      closeAppModal();
    }
  });

  dialog.addEventListener("close", () => {
    // close() then showModal() in one turn: the close event can land after
    // the new content is inserted and wipe it, leaving an empty open shell.
    if (dialog.open) return;
    clearContent();
  });

  document.addEventListener("wheel", preventBackgroundWheel, { capture: true, passive: false });
  document.addEventListener("touchmove", preventBackgroundTouch, { capture: true, passive: false });

  const showModal = HTMLDialogElement.prototype.showModal;
  HTMLDialogElement.prototype.showModal = function showModalAndPin() {
    showModal.call(this);
    syncBackgroundScroll();
  };
  const closeModal = HTMLDialogElement.prototype.close;
  HTMLDialogElement.prototype.close = function closeAndUnpin(...args) {
    closeModal.apply(this, args);
    syncBackgroundScroll();
  };

  maybeAutoOpen();
  syncBackgroundScroll();
}

const appModal = { open: openAppModal, close: closeAppModal };

if (typeof document !== "undefined") {
  window.appModal = appModal;
  initAppModal();
}

export { openAppModal, closeAppModal, appModal, backgroundScrollShouldLock };
